import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import { I18n, Translate } from 'common'
import { observer } from 'mobx-react-lite'
import { ReactElement, useState } from 'react'
import { useSelector } from 'react-redux'
import { defaultTheme } from 'src/theme'
import { Price } from 'src/ui-components'
import { RootState } from 'store'
import styled from 'styled-components'
import { getCartLaborCost, getVehicleGrandTotal } from '../store/cartSelectors'
import { SellerCartVehicle } from '../store/ISellerCart'
import { SellerPriceDetails } from './SellerPriceDetails'
import { formatVehicleDisplayWithOutEngine } from 'src/helpers/utils'

interface SellerCartVehicleInfoProps {
  sellerCartVehicle: SellerCartVehicle
}

export const SellerCartVehicleInfo = observer(
  (props: SellerCartVehicleInfoProps): ReactElement => {
    const { sellerCartVehicle } = props
    const { vehicle } = sellerCartVehicle
    const [expanded, setExpanded] = useState<boolean>(false)

    const vehicleName = vehicle.year?.id
      ? formatVehicleDisplayWithOutEngine(vehicle)
      : Translate('details')

    const { vehicleGrandtotal, laborSubTotal } = useSelector(
      (state: RootState) => ({
        vehicleGrandtotal: getVehicleGrandTotal(state, vehicle),
        laborSubTotal: getCartLaborCost(state, vehicle),
      })
    )

    return (
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        elevation={0}
        sx={{
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
          style={{ padding: 0 }}
        >
          <div
            style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
          >
            <span>
              {!expanded ? (
                <StyledExpandMore
                  sx={{ margin: 0 }}
                  style={{ color: defaultTheme.colors.link }}
                />
              ) : (
                <StyledExpandLess
                  sx={{ margin: 0 }}
                  style={{ color: defaultTheme.colors.link }}
                />
              )}
              <Link>{vehicleName}</Link>
            </span>

            {!expanded && (
              <GrandTotalWrapper>
                <I18n name="vehicleTotal" />
                <Price num={vehicleGrandtotal} />
              </GrandTotalWrapper>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails style={{ padding: 0 }}>
          {sellerCartVehicle.sellers?.map((seller) => (
            <SellerPriceDetails
              key={`${seller.coverageKey}_${seller.sellerName}`}
              sellerWithLocations={seller}
              vehicle={vehicle}
            />
          ))}
          {laborSubTotal > 0 && (
            <GrandTotalWrapper noMargin>
              <I18n name="labor" />
              <Price num={laborSubTotal} />
            </GrandTotalWrapper>
          )}
          <GrandTotalWrapper noMargin>
            <I18n name="vehicleTotal" />
            <Price num={vehicleGrandtotal} />
          </GrandTotalWrapper>
        </AccordionDetails>
      </Accordion>
    )
  }
)

const StyledExpandMore = styled(ExpandMore)`
  margin-top: -3px;
  margin-right: 10px;
  color: #0f172a;
  cursor: pointer;
`

const StyledExpandLess = styled(ExpandLess)`
  margin-top: -3px;
  margin-right: 10px;
  color: #0f172a;
  cursor: pointer;
`

const Link = styled.a`
  font-family: 'Roboto';
  font-weight: bold;
  font-size: 14px;
  line-height: 18px;
  text-decoration-line: underline;
  color: ${defaultTheme.colors.link};
  mix-blend-mode: normal;
  cursor: pointer;
`

interface VehicleGrandTotalProps {
  noMargin?: boolean
}

const GrandTotalWrapper = styled.div<VehicleGrandTotalProps>`
  font-family: 'Roboto';
  font-weight: bold;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  margin-left: 36px;
  margin-right: ${(p) => (p.noMargin ? '0px' : '2px')};
  margin-top: 10px;
`
